
import { useState, useEffect } from 'react';

interface OrganizationFormModalProps {
  onClose: () => void;
}

const FORM_URL = 'https://readdy.ai/api/form/d6ddrk2ff40lgbk6dbmg';

export default function OrganizationFormModal({ onClose }: OrganizationFormModalProps) {
  const [formData, setFormData] = useState({
    organization_name: '',
    representative_name: '',
    phone: '',
    opinion: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'opinion' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.organization_name.trim()) {
      setError('단체명을 입력해주세요.');
      return;
    }
    if (!formData.representative_name.trim()) {
      setError('대표자 이름을 입력해주세요.');
      return;
    }
    if (!formData.phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('organization_name', formData.organization_name.trim());
      body.append('representative_name', formData.representative_name.trim());
      body.append('phone', formData.phone.trim());
      body.append('opinion', formData.opinion.trim());

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!res.ok) throw new Error('제출 실패');
      setIsSuccess(true);
    } catch {
      setError('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div
        className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp"
        style={{
          animation: 'slideUp 0.35s ease-out',
        }}
      >
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3
            className="text-lg font-bold text-gray-900"
            style={{ fontFamily: "'Nanum Myeongjo', serif" }}
          >
            참여 단체 신청
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500"></i>
          </button>
        </div>

        {isSuccess ? (
          <div className="p-6 md:p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-500"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">신청이 완료되었습니다</h4>
            <p className="text-sm text-gray-500 mb-6">
              검토 후 참여 단체 목록에 추가됩니다.<br />감사합니다.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              닫기
            </button>
          </div>
        ) : (
          <form
            id="organization-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="p-6 space-y-5"
          >
            <p className="text-sm text-gray-500 leading-relaxed">
              포괄적 차별금지법 반대 성명에 함께할 단체를 신청해주세요.
            </p>

            {/* 단체명 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                단체명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
                placeholder="단체명을 입력해주세요"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {/* 대표자 이름 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                대표자 이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="representative_name"
                value={formData.representative_name}
                onChange={handleChange}
                placeholder="대표자 이름을 입력해주세요"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {/* 전화번호 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                전화번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="010-0000-0000"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {/* 의견 */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                의견
              </label>
              <textarea
                name="opinion"
                value={formData.opinion}
                onChange={handleChange}
                placeholder="전하고 싶은 의견이 있으시면 작성해주세요 (선택)"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {formData.opinion.length}/500
              </p>
            </div>

            {error && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  제출 중...
                </span>
              ) : (
                '참여 단체 신청하기'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
